// WorkingHoursSection.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const WorkingHoursSection = ({ form, setForm, errors, handleInputChange }) => {
  const { t, i18n } = useTranslation();
  const [hoveredDay, setHoveredDay] = useState(null);
  const [mode, setMode] = useState("advanced");

  const handleDayChange = (day, field) => (e) => {
    const value = field === "enabled" ? e.target.checked : e.target.value;
    setForm((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: {
          ...prev.workingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const timeOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hh = String(h).padStart(2, "0");
        const mm = String(m).padStart(2, "0");
        options.push(`${hh}:${mm}`);
      }
    }
    return options;
  }, []);

  const renderTimeSelect = (value, onChange) => (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        padding: "10px 12px",
        border: "2px solid #bfdbfe",
        borderRadius: "8px",
        fontSize: "15px",
        backgroundColor: "#fff",
        outline: "none",
        color: "#1f2937",
        boxSizing: "border-box"
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#3b82f6";
        e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "#bfdbfe";
        e.target.style.boxShadow = "none";
      }}
    >
      <option value="" disabled>{t("clinicSetup.workingHours.selectTime")}</option>
      {timeOptions.map((t) => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
  );

  const applyGroupedHours = ({ targetDays, enabled, from, to }) => {
    setForm((prev) => {
      const updated = { ...prev.workingHours };
      targetDays.forEach((d) => {
        updated[d] = { ...updated[d], enabled: enabled ?? updated[d].enabled, from: from ?? updated[d].from, to: to ?? updated[d].to };
      });
      return { ...prev, workingHours: updated };
    });
  };

  const dayLabels = {
    sunday: t("clinicSetup.workingHours.days.sunday"),
    monday: t("clinicSetup.workingHours.days.monday"),
    tuesday: t("clinicSetup.workingHours.days.tuesday"),
    wednesday: t("clinicSetup.workingHours.days.wednesday"),
    thursday: t("clinicSetup.workingHours.days.thursday"),
    friday: t("clinicSetup.workingHours.days.friday"),
    saturday: t("clinicSetup.workingHours.days.saturday"),
  };

  const handleCopyToAll = (sourceDay) => {
    const sourceHours = form.workingHours[sourceDay];
    setForm((prev) => {
      const newWorkingHours = { ...prev.workingHours };
      days.forEach((day) => {
        newWorkingHours[day] = {
          ...sourceHours,
          enabled: prev.workingHours[day].enabled
        };
      });
      return {
        ...prev,
        workingHours: newWorkingHours
      };
    });
  };

  const handleApplyToWeekdays = (sourceDay) => {
    const sourceHours = form.workingHours[sourceDay];
    const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"];
    setForm((prev) => {
      const newWorkingHours = { ...prev.workingHours };
      weekdays.forEach((day) => {
        newWorkingHours[day] = {
          ...sourceHours,
          enabled: prev.workingHours[day].enabled
        };
      });
      return {
        ...prev,
        workingHours: newWorkingHours
      };
    });
  };

  const applyPreset = (preset) => {
    if (preset === "24_7") {
      setForm((prev) => {
        const updated = { ...prev.workingHours };
        days.forEach((d) => {
          updated[d] = { ...updated[d], enabled: true, from: "00:00", to: "23:59" };
        });
        return { ...prev, workingHours: updated };
      });
      return;
    }
    if (preset === "clear") {
      setForm((prev) => {
        const updated = { ...prev.workingHours };
        days.forEach((d) => {
          updated[d] = { ...updated[d], enabled: false, from: "", to: "" };
        });
        return { ...prev, workingHours: updated };
      });
      return;
    }
    if (preset === "mon_fri_9_5") {
      setForm((prev) => {
        const updated = { ...prev.workingHours };
        days.forEach((d) => {
          const isWeekday = ["monday","tuesday","wednesday","thursday","friday"].includes(d);
          updated[d] = {
            ...updated[d],
            enabled: isWeekday,
            from: isWeekday ? "09:00" : updated[d].from,
            to: isWeekday ? "17:00" : updated[d].to
          };
        });
        return { ...prev, workingHours: updated };
      });
      return;
    }
    if (preset === "all_10_6") {
      setForm((prev) => {
        const updated = { ...prev.workingHours };
        days.forEach((d) => {
          updated[d] = { ...updated[d], enabled: true, from: "10:00", to: "18:00" };
        });
        return { ...prev, workingHours: updated };
      });
    }
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "24px",
        width: "100%",
        maxWidth: "960px",
        margin: "0 auto",
      }}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "24px",
        gap: "12px",
        flexWrap: "wrap"
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <h3 style={{
            fontSize: "18px", 
            fontWeight: "600", 
            color: "#1f2937",
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "4px"
          }}>
            {t("clinicSetup.workingHours.title")}
            <span style={{ color: "#ef4444", fontSize: "16px" }}>*</span>
          </h3>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => applyPreset("all_10_6")}
            style={{ padding: "6px 10px", borderRadius: "9999px", border: "1px solid #e5e7eb", background: "#fff", fontSize: "12px", fontWeight: 600, color: "#374151", cursor: "pointer" }}
            onMouseEnter={(e)=>{ e.target.style.borderColor="#9ca3af"; }}
            onMouseLeave={(e)=>{ e.target.style.borderColor="#e5e7eb"; }}
          >
            {t("clinicSetup.workingHours.presets.allDays")}
          </button>
          <button
            type="button"
            onClick={() => applyPreset("24_7")}
            style={{ padding: "6px 10px", borderRadius: "9999px", border: "1px solid #bfdbfe", background: "#eff6ff", fontSize: "12px", fontWeight: 600, color: "#1d4ed8", cursor: "pointer" }}
            onMouseEnter={(e)=>{ e.target.style.borderColor="#60a5fa"; }}
            onMouseLeave={(e)=>{ e.target.style.borderColor="#bfdbfe"; }}
          >
            {t("clinicSetup.workingHours.presets.fullDay")}
          </button>
          <button
            type="button"
            onClick={() => applyPreset("clear")}
            style={{ padding: "6px 10px", borderRadius: "9999px", border: "1px solid #fca5a5", background: "#fff1f2", fontSize: "12px", fontWeight: 600, color: "#b91c1c", cursor: "pointer" }}
            onMouseEnter={(e)=>{ e.target.style.borderColor="#ef4444"; }}
            onMouseLeave={(e)=>{ e.target.style.borderColor="#fca5a5"; }}
          >
            {t("clinicSetup.workingHours.presets.clear")}
          </button>
        </div>
      </div>
      {/* Per-day cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(220px, 1fr))",
        gap: "20px"
      }}>
        {days.map((day) => {
          const isEnabled = form.workingHours[day].enabled;
          const isHovered = hoveredDay === day;
          
          return (
            <div
              key={day}
              style={{
                background: isEnabled ? "rgba(0,212,179,0.08)" : "#f9fafb",
                border: isEnabled 
                  ? (isHovered ? "2px solid rgba(0,212,179,0.7)" : "2px solid rgba(0,212,179,0.35)")
                  : "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "24px",
                minHeight: "170px",
                transition: "all 0.2s ease",
                cursor: "pointer",
                position: "relative"
              }}
              onMouseEnter={() => setHoveredDay(day)}
              onMouseLeave={() => setHoveredDay(null)}
            >
              {/* Day Header with Toggle and Status */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: isEnabled ? "#0f766e" : "#374151"
                  }}>{dayLabels[day]}</div>
                </div>
                <label 
                  className="clinicsetup-switch"
                  onClick={(e) => e.stopPropagation()}
                  style={{ margin: 0 }}
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={handleDayChange(day, "enabled")}
                  />
                  <span className="clinicsetup-slider"></span>
                </label>
              </div>

              {/* Time Selects */}
              {isEnabled ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{t("clinicSetup.workingHours.opening")}</label>
                    {renderTimeSelect(form.workingHours[day].from, (v) => handleDayChange(day, "from")({ target: { value: v } }))}
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: "500", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{t("clinicSetup.workingHours.closing")}</label>
                    {renderTimeSelect(form.workingHours[day].to, (v) => handleDayChange(day, "to")({ target: { value: v } }))}
                  </div>
                  
                  {/* Quick Actions - Always visible */}
                  <div style={{ gridColumn: "1 / -1", marginTop: "6px", paddingTop: "10px", borderTop: "1px solid rgba(0,212,179,0.25)", display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyToAll(day);
                        }}
                        style={{
                          flex: 1,
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          color: "#3b82f6",
                          background: "#fff",
                          border: "1px solid #bfdbfe",
                          borderRadius: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#eff6ff";
                          e.target.style.borderColor = "#3b82f6";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#fff";
                          e.target.style.borderColor = "#bfdbfe";
                        }}
                      >
                        {t("clinicSetup.workingHours.copyToAll")}
                      </button>
                      
                    </div>
                </div>
              ) : (
                <div style={{
                  fontSize: "13px",
                  color: "#9ca3af",
                  fontStyle: "italic",
                  textAlign: "center",
                  padding: "16px 0"
                }}>
                  {t("clinicSetup.workingHours.closed")}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {errors.workingHours && (
        <div style={{ 
          color: "#ef4444", 
          fontSize: "14px", 
          marginTop: "20px",
          textAlign: "center",
          fontWeight: "500"
        }}>
          {errors.workingHours}
        </div>
      )}
    </div>
  );
};

export default WorkingHoursSection;
